import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@acto/web-common/locales";
import type { StoryCard as StoryCardSchema } from "@acto/genui-components-schema";
import { MeetingPrepStoryRemovalReason } from "@acto/gql/graphql";
import {
  useRemoveMeetingPrepStory,
  useRestoreMeetingPrepStory,
} from "@acto/taskpane/core/data-access/meeting-prep-story-removals";
import type { MeetingPrepCardHide, MeetingPrepCardReveal, MeetingPrepLearnedDefault } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { StoryCard } from "./StoryCard";
import { ReasonPrompt, TRACKED_REASON_BY_KIND, type HideReasonChoice, type HideReasonKind } from "./ReasonPrompt";
import { StoryCardRemovedNotice } from "./StoryCardRemovedNotice";

const REASON_BY_KIND: Record<HideReasonKind, MeetingPrepStoryRemovalReason> = {
  contentNotRelevant: MeetingPrepStoryRemovalReason.ContentNotRelevant,
  dataIncorrect: MeetingPrepStoryRemovalReason.DataIncorrect,
};

const REMOVAL_LOCALE = "fieldSalesTwo.genuiMeetingPrep.storyCard.removal";

const AUTO_DISMISS_MS = 5000;

type Phase = "idle" | "reason" | "removed";

interface RemovableStoryCardProps {
  card: StoryCardSchema;
  clientId: string;
  /** Session id of the generated prep; per-card hides are scoped to it. */
  prepSessionId?: string | null;
  /** The viewer's ACTIVE card hides for the client, applied per talking point. */
  cardHides?: MeetingPrepCardHide[];
  cardReveals?: MeetingPrepCardReveal[];
  learnedDefaults?: MeetingPrepLearnedDefault[];
  /** The card entered the removed state and shows the undo notice. */
  onRemoved: (storyKey: string) => void;
  /** The removal was undone (or the mutation failed). */
  onRestored: (storyKey: string) => void;
  /** The undo window elapsed; the removal is final and the card is masked away. */
  onDismissed: (storyKey: string) => void;
}

export function RemovableStoryCard({
  card,
  clientId,
  prepSessionId,
  cardHides,
  cardReveals,
  learnedDefaults,
  onRemoved,
  onRestored,
  onDismissed,
}: RemovableStoryCardProps) {
  const t = useTranslations(REMOVAL_LOCALE);
  const storyKey = card.storyKey;
  const [phase, setPhase] = useState<Phase>("idle");
  const { removeMeetingPrepStory } = useRemoveMeetingPrepStory();
  const { restoreMeetingPrepStory } = useRestoreMeetingPrepStory();
  const track = useTrack();
  const pendingRemovalRef = useRef<Promise<{ id: string } | null | undefined> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Held until the removal is final and cleared the moment undo is tapped, so an undone
  // removal is never tracked or dismissed, even while its request is still open.
  const pendingFeedbackRef = useRef<HideReasonChoice | null>(null);

  const trackFeedbackGiven = () => {
    const feedback = pendingFeedbackRef.current;
    if (!feedback) return;
    pendingFeedbackRef.current = null;
    track(AnalyticsEvents.MEETING_PREP_STORY_FEEDBACK_GIVEN, {
      customerId: clientId,
      storyType: card.category,
      reason: TRACKED_REASON_BY_KIND[feedback.kind],
      reasonText: feedback.text,
      cardCount: card.talkingPoints.length,
    });
  };
  // The unmount cleanup closes over the first render, so it calls the latest trackFeedbackGiven through this ref.
  const trackFeedbackGivenRef = useRef(trackFeedbackGiven);
  useEffect(() => {
    trackFeedbackGivenRef.current = trackFeedbackGiven;
  });

  useEffect(
    () => () => {
      if (!dismissTimerRef.current) return;
      clearTimeout(dismissTimerRef.current);
      // Unmounting inside the undo window keeps the removal, so its feedback is still tracked once the request succeeds.
      void pendingRemovalRef.current?.then((removal) => {
        if (removal) trackFeedbackGivenRef.current();
      });
    },
    [],
  );

  if (!storyKey) {
    return (
      <StoryCard
        card={card}
        clientId={clientId}
        prepSessionId={prepSessionId}
        cardHides={cardHides}
        cardReveals={cardReveals}
        learnedDefaults={learnedDefaults}
      />
    );
  }

  const clearDismissTimer = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  };

  const scheduleDismiss = () => {
    dismissTimerRef.current = setTimeout(() => void handleDismiss(), AUTO_DISMISS_MS);
  };

  const handleDismiss = async () => {
    const removal = await pendingRemovalRef.current;
    const wasUndone = pendingFeedbackRef.current === null;
    // A failed removal already reverted to idle.
    if (removal === null || wasUndone) return;
    trackFeedbackGiven();
    onDismissed(storyKey);
  };

  const handleReasonSelect = (choice: HideReasonChoice) => {
    setPhase("removed");
    onRemoved(storyKey);
    pendingFeedbackRef.current = choice;

    const removal = removeMeetingPrepStory({
      clientId,
      storyKey,
      reason: REASON_BY_KIND[choice.kind],
      reasonText: choice.text,
      storyCard: JSON.stringify(card),
    });
    pendingRemovalRef.current = removal;

    void removal.then((result) => {
      if (result === null) {
        clearDismissTimer();
        pendingRemovalRef.current = null;
        pendingFeedbackRef.current = null;
        setPhase("idle");
        onRestored(storyKey);
      }
    });

    scheduleDismiss();
  };

  const handleUndo = async () => {
    clearDismissTimer();
    const feedback = pendingFeedbackRef.current;
    pendingFeedbackRef.current = null;
    const removal = await pendingRemovalRef.current;
    if (removal) {
      const restored = await restoreMeetingPrepStory(removal.id);
      if (restored === null) {
        // The restore failed and the removal stands: it is re-armed to dismiss and be tracked unless a retry succeeds.
        pendingFeedbackRef.current = feedback;
        scheduleDismiss();
        return;
      }
    }
    pendingRemovalRef.current = null;
    setPhase("idle");
    onRestored(storyKey);
  };

  switch (phase) {
    case "idle":
      return (
        <StoryCard
          card={card}
          clientId={clientId}
          prepSessionId={prepSessionId}
          cardHides={cardHides}
          cardReveals={cardReveals}
          learnedDefaults={learnedDefaults}
          onRemoveRequest={() => setPhase("reason")}
        />
      );
    case "reason":
      return (
        <ReasonPrompt
          question={t("reasonQuestion")}
          namespace={REMOVAL_LOCALE}
          onSelect={handleReasonSelect}
          onCancel={() => setPhase("idle")}
        />
      );
    case "removed":
      return <StoryCardRemovedNotice onUndo={() => void handleUndo()} />;
  }
}
